import React, { useState, useEffect } from 'react';
import { AnimatePresence } from 'framer-motion';
import {
  Section,
  Container,
  TopSection,
  BottomSection,
  LeftColumn,
  RightColumn,
  Title,
  Description,
  CardsContainer,
  Card,
  CardHeader,
  CardTitle,
  ToggleIcon,
  CardContent,
  CardText,
  ImagesContainer,
  SmallImage,
  LargeImage,
  Image,
} from './DiscoverSection.styled';
import { DISCOVER_CARDS } from './constant';
import { CATEGORIES } from '../Categories/constant';

const DiscoverSection = () => {
  const [expandedCard, setExpandedCard] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Get all 7 category images
  const categoryImages = CATEGORIES.map((category) => category.image);

  // Rotate images every 5 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % categoryImages.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [categoryImages.length]);

  const toggleCard = (index) => {
    setExpandedCard(expandedCard === index ? null : index);
  };

  // Get the two images to display (current and next)
  const getCurrentImages = () => {
    const firstIndex = currentImageIndex;
    const secondIndex = (currentImageIndex + 1) % categoryImages.length;
    return {
      first: categoryImages[firstIndex],
      second: categoryImages[secondIndex],
      firstIndex,
      secondIndex,
    };
  };

  const { first, second, firstIndex, secondIndex } = getCurrentImages();

  const contentVariants = {
    collapsed: {
      height: 0,
      opacity: 0,
    },
    expanded: {
      height: 'auto',
      opacity: 1,
      marginTop: 16,
    },
  };

  return (
    <Section>
      <Container>
        {/* Top Section: Title and Description */}
        <TopSection>
          <LeftColumn
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0 }}
            transition={{ duration: 0.8, ease: [0.25, 0.1, 0.25, 1] }}
          >
            <Title>DISCOVER SWAN</Title>
          </LeftColumn>

          <RightColumn
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0 }}
            transition={{
              duration: 0.8,
              delay: 0.1,
              ease: [0.25, 0.1, 0.25, 1],
            }}
          >
            <Description>
              <strong className="strong-title">55+ years later. Still holding things together.</strong><br /> High-quality support systems engineered for Plumbing, Electrical, HVAC, and Firefighting applications.
            </Description>
          </RightColumn>
        </TopSection>

        {/* Bottom Section: Cards and Images */}
        <BottomSection>
          <LeftColumn
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.1 }}
            transition={{ duration: 0.8, ease: [0.25, 0.1, 0.25, 1] }}
          >
            <CardsContainer>
              {DISCOVER_CARDS.map((card, index) => (
                <Card key={index} onClick={() => toggleCard(index)}>
                  <CardHeader>
                    <CardTitle>{card.title}</CardTitle>
                    <ToggleIcon>
                      {expandedCard === index ? '−' : '+'}
                    </ToggleIcon>
                  </CardHeader>
                  <CardContent
                    variants={contentVariants}
                    animate={expandedCard === index ? 'expanded' : 'collapsed'}
                    transition={{ duration: 0.3 }}
                  >
                    <CardText>{card.content}</CardText>
                  </CardContent>
                </Card>
              ))}
            </CardsContainer>
          </LeftColumn>

          <RightColumn
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.1 }}
            transition={{
              duration: 0.8,
              delay: 0.2,
              ease: [0.25, 0.1, 0.25, 1],
            }}
          >
            <ImagesContainer>
              <SmallImage>
                <AnimatePresence mode="sync" initial={false}>
                  <Image
                    key={`small-${firstIndex}`}
                    src={first}
                    alt="Discover Swan 1"
                    initial={{ opacity: 0, x: 150 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -250 }}
                    transition={{
                      duration: 1,
                      ease: [0.25, 0.1, 0.25, 1],
                    }}
                  />
                </AnimatePresence>
              </SmallImage>
              <LargeImage>
                <AnimatePresence mode="sync" initial={false}>
                  <Image
                    key={`large-${secondIndex}`}
                    src={second}
                    alt="Discover Swan 2"
                    initial={{ opacity: 0, x: 200 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{
                      opacity: 0,
                      x: -250,
                      y: -80,
                      scale: 0.65,
                    }}
                    transition={{
                      duration: 1,
                      ease: [0.25, 0.1, 0.25, 1],
                    }}
                  />
                </AnimatePresence>
              </LargeImage>
            </ImagesContainer>
          </RightColumn>
        </BottomSection>
      </Container>
    </Section>
  );
};

export default DiscoverSection;
